import React, { createContext, useContext, ReactNode } from 'react';
import { Stage, Layer, Rect as KonvaRect, Circle as KonvaCircle, Text as KonvaText, Group, Line } from 'react-konva';

export interface Point {
  x: number;
  y: number;
}

export interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Circle {
  x: number;
  y: number;
  radius: number;
}

export class CanvasCollision {
  static pointInRect(point: Point, rect: Rectangle): boolean {
    return point.x >= rect.x &&
           point.x <= rect.x + rect.width &&
           point.y >= rect.y &&
           point.y <= rect.y + rect.height;
  }

  static rectIntersect(rect1: Rectangle, rect2: Rectangle): boolean {
    return rect1.x < rect2.x + rect2.width &&
           rect1.x + rect1.width > rect2.x &&
           rect1.y < rect2.y + rect2.height &&
           rect1.y + rect1.height > rect2.y;
  }

  static circleIntersect(circle1: Circle, circle2: Circle): boolean {
    const dx = circle1.x - circle2.x;
    const dy = circle1.y - circle2.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < circle1.radius + circle2.radius;
  }

  static pointInCircle(point: Point, circle: Circle): boolean {
    const dx = point.x - circle.x;
    const dy = point.y - circle.y;
    return Math.sqrt(dx * dx + dy * dy) <= circle.radius;
  }

  static getDistance(point1: Point, point2: Point): number {
    const dx = point1.x - point2.x;
    const dy = point1.y - point2.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
}

interface CollisionContextType {
  pointInRect: (point: Point, rect: Rectangle) => boolean;
  rectIntersect: (rect1: Rectangle, rect2: Rectangle) => boolean;
  circleIntersect: (circle1: Circle, circle2: Circle) => boolean;
  pointInCircle: (point: Point, circle: Circle) => boolean;
  getDistance: (point1: Point, point2: Point) => number;
}

const CollisionContext = createContext<CollisionContextType | null>(null);

interface CollisionProviderProps {
  children: ReactNode;
}

export const CollisionProvider: React.FC<CollisionProviderProps> = ({ children }) => {
  const collisionMethods: CollisionContextType = {
    pointInRect: CanvasCollision.pointInRect,
    rectIntersect: CanvasCollision.rectIntersect,
    circleIntersect: CanvasCollision.circleIntersect,
    pointInCircle: CanvasCollision.pointInCircle,
    getDistance: CanvasCollision.getDistance,
  };

  return (
    <CollisionContext.Provider value={collisionMethods}>
      <div data-collision-system="kinzy">{children}</div>
    </CollisionContext.Provider>
  );
};

export const useCollision = (): CollisionContextType => {
  const context = useContext(CollisionContext);
  if (!context) {
    throw new Error('useCollision must be used within a CollisionProvider');
  }
  return context;
};

interface CanvasGameProps {
  width: number;
  height: number;
  children: React.ReactNode;
  onStageClick?: (e: any) => void;
  className?: string;
}

export const CanvasGame: React.FC<CanvasGameProps> = ({
  width,
  height,
  children,
  onStageClick,
  className = ''
}) => {
  return (
    <div className={`game-canvas-container ${className}`}>
      <Stage
        width={width}
        height={height}
        onClick={onStageClick}
        style={{ touchAction: 'none' }}
      >
        <Layer>
          {children}
        </Layer>
      </Stage>
    </div>
  );
};

interface RectProps {
  x?: number;
  y?: number;
  width: number;
  height: number;
  fill?: string;
  opacity?: number;
  cornerRadius?: number;
}

interface CircleProps {
  x: number;
  y: number;
  radius: number;
  fill?: string;
  opacity?: number;
}

interface TextProps {
  x: number;
  y: number;
  text: string;
  fontSize?: number;
  fill?: string;
  align?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
}

export const Rect: React.FC<RectProps> = (props) => {
  return <KonvaRect {...props} />;
};

export const Circle: React.FC<CircleProps> = (props) => {
  return <KonvaCircle {...props} />;
};

export const Text: React.FC<TextProps> = (props) => {
  return <KonvaText {...props} />;
};

export { Group, Line };

export type { CanvasGameProps, CollisionProviderProps };

export default CanvasGame;